import React, { useState } from 'react';
import { css } from '@emotion/css';
import OrganizationModel from '../../../models/organizations/api/OrganizationModel';
import { dark_grey_blue, ocean_blue, color_shades_lighter } from '../../../constants/colors';
import { Link } from 'react-router-dom';
import HorizontalSeparator from '../../../components/structure/HorizontalSeparator';
import UserContainer from '../../../state/containers/UserContainer';
import OrganizationContainer from '../../../state/containers/OrganizationContainer';
import styled from '@emotion/styled';
import CreateOrganizationModal from '../../../components/general/CreateOrganizationModal';
import DisabledOrganizationSearchModal from './DisabledOrganizationSearchModal';
import SparkContainer from '../../../hooks/SparkContainer';

const addOrgIcon = require('../../../content/images/add-org/add-org.svg');
const organizationIcon = require('../../../content/images/account-settings/organization-dark.svg');
const searchIcon = require('../../../content/images/search.svg');

interface OrganizationsMenuItemProps {
    currentOrganization: OrganizationModel
    organizations: OrganizationModel[]
    emptyMessage?: string
    onOrganizationSelected: (org: OrganizationModel) => void
    onDisabledOrganizationSelected: (org: OrganizationModel) => Promise<void>
    userContainer: UserContainer
    organizationContainer: OrganizationContainer
}

const OrganizationsMenuItem: React.FC<OrganizationsMenuItemProps> = (props) => {

    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showDisabledSearchModal, setShowDisabledSearchModal] = useState(false);
    const sparkContainer = SparkContainer.useContainer();
    const canManageOrganizations = props.userContainer.state.currentUser?.isPlatformOperator === true;
    const canFindDisabledOrganizations = canManageOrganizations && !sparkContainer.inSpark;

    const renderHorizontalSeparator = (index: number) => {
        if (index !== props.organizations.length - 1) {
            return <HorizontalSeparator />
        } else {
            if (canManageOrganizations) {
                return <HorizontalSeparator />
            }
        }
    };

    const handleNewOrgSetupClick = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        e.preventDefault();
        e.stopPropagation();
        setShowCreateModal(true);
    };

    const handleFindDisabledClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        e.preventDefault();
        e.stopPropagation();
        setShowDisabledSearchModal(true);
    };

    return (
        <div className={containerStyle}>
            <div className={listContainerStyle}>
                <div className={listInnerContainerStyle}>
                    {props.organizations.length > 0 ? props.organizations.map((org, index) =>
                        <div key={org.id}>
                            <Link to="/v" className={`${listItemStyle} ${org.id == props.currentOrganization?.id ? currentOrgStyle : ''}`} onClick={() => props.onOrganizationSelected(org)}>
                                <p>{org.name}</p>
                            </Link>
                            {renderHorizontalSeparator(index)}
                        </div>
                    ) : <p className={emptyLabelStyle}>{props.emptyMessage ?? "This is where you'll see other organizations you're a member of."}</p>}
                    {canManageOrganizations ?
                        (
                            <>
                                <Link to="/v" className={addOrgListItemStyle} onClick={(e) => handleNewOrgSetupClick(e)}>
                                    <p>Create a New Organization</p>
                                    <AddOrgIcon src={addOrgIcon} alt="Add Organization" />
                                    <OrgIcon src={organizationIcon} alt="Organization" />
                                </Link>
                                {canFindDisabledOrganizations && (
                                    <>
                                        <HorizontalSeparator />
                                        <button className={`${addOrgListItemStyle} ${recoveryButtonStyle}`} type="button" onClick={handleFindDisabledClick}>
                                            <p>Find a Disabled Organization</p>
                                            <OrgIcon src={searchIcon} alt="Find Disabled Organization" />
                                        </button>
                                    </>
                                )}
                            </>
                        )
                        : null
                    }
                </div>
            </div>
            <CreateOrganizationModal
                isVisible={showCreateModal}
                onClose={() => setShowCreateModal(false)}
                onOrganizationCreated={props.onOrganizationSelected}
            />
            <DisabledOrganizationSearchModal
                isVisible={showDisabledSearchModal}
                onClose={() => setShowDisabledSearchModal(false)}
                onOrganizationSelected={props.onDisabledOrganizationSelected}
                organizationContainer={props.organizationContainer}
            />
        </div>
    )
};

const AddOrgIcon = styled.img`
    margin-right: 8px;
`;

const OrgIcon = styled.img`
    margin-right: 22px;
`;

const listContainerStyle = css`
    padding: 32px;
    background: ${color_shades_lighter};
`;

const listInnerContainerStyle = css`
    border-radius: 8px;
    background-color: white;
    border: solid 1px #cccccc;
`;

const addOrgListItemStyle = css`
    height: 48px;
    font-family: Muli;
    font-size: 14px;
    font-weight: 600;
    font-style: normal;
    font-stretch: normal;
    line-height: normal;
    letter-spacing: normal;
    text-align: right;
    color: ${dark_grey_blue};
    display: flex;
    align-items: center;
    p {
        margin-right: 8px;
        margin-left: auto;
    }
    &:hover {
        color: ${ocean_blue};
    }
`;

const recoveryButtonStyle = css`
    background: white;
    border: 0;
    cursor: pointer;
    padding: 0;
    width: 100%;
`;

const listItemStyle = css`
    height: 48px;
    font-family: Muli;
    font-size: 14px;
    font-weight: 600;
    font-style: normal;
    font-stretch: normal;
    line-height: normal;
    letter-spacing: normal;
    text-align: right;
    color: ${dark_grey_blue};
    display: flex;
    align-items: center;
    p {
        margin-right: 22px;
        margin-left: auto;
    }
    p:hover {
        color: ${ocean_blue};
    }
`;

const emptyLabelStyle = css`
    height: 48px;
    font-family: Muli;
    font-size: 14px;
    font-weight: 600;
    font-style: normal;
    font-stretch: normal;
    line-height: normal;
    letter-spacing: normal;
    text-align: center;
    color: ${dark_grey_blue};
    display: flex;
    align-items: center;
    padding: 8px;
`

const currentOrgStyle = css`
    color: ${ocean_blue};
`;

const containerStyle = css`
    overflow: hidden;
    border-radius: 0 0 20px 20px;
    margin-bottom: 124px;
`;

export default OrganizationsMenuItem;
